/**
 * A base class that provides all the lifecycle hooks for the basket booking journey.
 * Each method should be overridden if any behaviour is required at any step.
 * @abstract
 */
class BaseFacet {
    /**
     * Create a facet instance
     */
    constructor() {
        this.skip = false;
        this.name = 'unnamed_facet';
        this.journey = null;
        this.hidden = false;
        this.navTemplate = null;
        this.mainTemplate = null;
        this.summaryTemplate = null;
    }

    /**
     * Called once when the booking journey starts.
     * @abstract
     * @param journey {object} An object which describes the current state of they booking journey
     * @returns {Promise<void>}
     */
    async onLoad(journey) {
        this.journey = journey;
    }

    /**
     * Called every time another facet has been updated
     * @abstract
     * @param journey {object} An object which describes the current state of they booking journey
     * @returns {Promise<void>}
     */
    async onUpdate(journey) {}

    /**
     * Check whether the conditions have been met to skip this step of the booking
     * @abstract
     * @returns {boolean}
     */
    isSkippable() {
        return this.skip;
    }

    /**
     * Is the step currently visible?
     * @abstract
     * @returns {boolean}
     */
    isShown() {
        return true;
    }

    /**
     * Is the step ready for checkout?
     * @abstract
     * @returns {boolean}
     */
    isReady() {
        return true;
    }

    /**
     * Called just prior to checking out the booking where each facet can add relevant data required to make the booking
     * @abstract
     * @param basket {object} The current basket details
     * @param item {object} The booking that is going to be made. Relevant data should be added to this object
     * @returns {Promise<void>}
     */
    async buildBasketItem(basket, item) {}

    /**
     * Called to reconstruct a facet's state from an existing basket item
     * @abstract
     * @param basket {object} The current basket details
     * @param item {object} The basket item that will become the booking
     * @returns {Promise<void>}
     */
    async reconstructItem(basket, item) {}

    /**
     * Called just prior to checkout where each facet can add relevant data required to modify the booking
     * @abstract
     * @param basket {object} The current basket details
     * @param item {object} The booking that is going to be modified. Relevant data should be added to this object
     * @returns {Promise<void>}
     */
    async buildModifyItem(basket, item) {}

    /**
     * An opportunity to do something when the booking journey is cancelled / completed.
     * @abstract
     * @param journey {object} An object which describes the current state of they booking journey
     * @returns {Promise<void>}
     */
    async onDestroy(journey) {
        delete this.journey;
    }
}

export {
    BaseFacet
};
